/**
 * Copyright (c) HashiCorp, Inc.
 * SPDX-License-Identifier: BUSL-1.1
 */

import Component from '@glimmer/component';
import {
  HdsTextBody,
  HdsIcon,
  HdsTooltipButton,
  HdsLinkInline,
} from '@hashicorp/design-system-components/components';
import type { IconName } from '@hashicorp/flight-icons/svg';

import './counter.scss';

export interface SSUReportingCounterSignature {
  Args: {
    count: number;
    title: string;
    tooltipMessage?: string;
    icon?: IconName;
    suffix?: string;
    link?: string;
    emptyText?: string;
    emptyLink?: string;
  };

  Blocks: {
    default: [];
  };

  Element: HTMLElement;
}
export default class SSUReportingCounter extends Component<SSUReportingCounterSignature> {
  get shouldShowEmptyState() {
    return this.args.count === 0 && this.args.emptyText;
  }

  get count() {
    if (this.shouldShowEmptyState) {
      return this.args.emptyText;
    }

    if (this.args.suffix) {
      return `${this.args.count} ${this.args.suffix}`;
    }

    return this.args.count;
  }

  get icon() {
    return this.args.icon || 'info';
  }

  get link() {
    if (this.shouldShowEmptyState && this.args.emptyLink) {
      return this.args.emptyLink;
    }
    return this.args.link;
  }

  <template>
    <div
      ...attributes
      data-test-vault-reporting-counter={{@title}}
      class="ssu-counter"
      aria-label="{{@title}} {{this.count}}"
    >
      <div class="ssu-counter__title-row">
        <HdsTextBody @weight="semibold" @size="200" @color="primary">{{@title}}
          {{#if @tooltipMessage}}
            <HdsTooltipButton
              data-test-vault-reporting-counter-tooltip-button
              class="ssu-counter__title-row__tooltip"
              @text={{@tooltipMessage}}
              aria-label="Tooltip for {{@title}}"
              @isInline={{true}}
            >
              <HdsIcon @name="help" @isInline={{true}} />
            </HdsTooltipButton>
          {{/if}}
        </HdsTextBody>
      </div>

      <HdsTextBody>
        {{#if this.link}}
          <HdsLinkInline
            @href={{this.link}}
            @color="secondary"
            class="ssu-counter__link"
            target="_self"
          >{{this.count}}
          </HdsLinkInline>
        {{else}}
          {{this.count}}
        {{/if}}
      </HdsTextBody>
    </div>
  </template>
}
